import React, { useCallback, useEffect, useState } from "react";
import { Loader2, AlertCircle, History, Info } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import api from "@/lib/api";
import { useTranslation } from "@/lib/i18n";
import { useAuth } from "@/context/AuthContext";
import { fmtCurrency, fmtNumber } from "@/components/portfolio/_format";

/**
 * Translate an internal `source` code into a user-friendly label.
 *
 * Phase 8.C-2 polish — the public transaction-history modal must never
 * surface internal identifiers like `migrated_legacy_opening_lot`.
 * Unrecognized values fall back to a neutral "Recorded transaction"
 * label rather than the raw string. Owner/Admin diagnostics get the
 * raw code via a separate annotation outside this helper.
 */
function _friendlySource(raw, t) {
  const key = String(raw || "").trim().toLowerCase();
  // Treat anything starting with "import" as an imported transaction
  // so future variants (import_csv, imported_broker, ...) all map
  // cleanly without a code change.
  const normalized = key.startsWith("import") ? "imported" : key;
  const labels = {
    migrated_legacy_opening_lot: t("portfolio.history.source_labels.migrated_legacy_opening_lot"),
    user_opening_lot: t("portfolio.history.source_labels.user_opening_lot"),
    user_additional_purchase: t("portfolio.history.source_labels.user_additional_purchase"),
    user_sell: t("portfolio.history.source_labels.user_sell"),
    imported: t("portfolio.history.source_labels.imported"),
  };
  return labels[normalized] || t("portfolio.history.source_labels.unknown");
}

/** Format a transaction date or fall back to a locale-aware placeholder. */
function _formatDate(rawDate, t) {
  if (!rawDate) return { display: t("portfolio.history.date_unavailable"), missing: true };
  const s = String(rawDate);
  if (!s.trim()) return { display: t("portfolio.history.date_unavailable"), missing: true };
  // Backend serializes datetimes as ISO 8601 ("2026-06-28T13:00:00+00:00"
  // or plain dates "2026-06-28"). We render only the YYYY-MM-DD slice.
  const isIsoish = /^\d{4}-\d{2}-\d{2}/.test(s);
  if (!isIsoish) return { display: t("portfolio.history.date_unavailable"), missing: true };
  return { display: s.slice(0, 10), missing: false };
}

/**
 * TransactionHistoryModal — Phase 8.C-2 (with polish for source labels +
 * missing-date fallback).
 *
 * Scoped per-ticker (or per-lifecycle when provided). Loads from
 * `GET /api/portfolio/holdings/{ticker}/transactions[?lifecycle_id=...]`
 * and renders newest-first. The backend already enriches each SELL row
 * with realized_pl / cost_basis_released / gross_amount / net_amount,
 * so we don't compute any aggregates here.
 *
 * Visual policy:
 *   • Color-neutral BUY/SELL badges (no green-for-BUY red-for-SELL).
 *   • The internal `source` field is hidden in the public view and
 *     exposed only as a friendly label via a Type-badge tooltip.
 *     Owner/Admin viewers additionally see the raw code in italics
 *     for diagnostics, since they are responsible for ledger
 *     integrity audits.
 *   • Missing transaction dates fall back to a localized
 *     "Date unavailable" string — the cell is never left blank.
 */
export default function TransactionHistoryModal({
  open,
  onOpenChange,
  ticker,
  lifecycleId = null,    // optional — restricts to one closed lifecycle
  lifecycleStatus = "open", // "open" | "closed"
  currency = "USD",
  onDateSaved,
}) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isAdmin = Boolean(user?.is_admin || user?.is_owner);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [txns, setTxns] = useState([]);

  const fetchHistory = useCallback(async () => {
    if (!ticker) return;
    setLoading(true);
    setError(null);
    try {
      const path = `/portfolio/holdings/${encodeURIComponent(ticker)}/transactions`;
      const params = lifecycleId ? { lifecycle_id: lifecycleId } : undefined;
      const r = await api.get(path, { params });
      setTxns(r?.data?.transactions || []);
    } catch (e) {
      setError(t("portfolio.history.error"));
      setTxns([]);
    } finally {
      setLoading(false);
    }
  }, [ticker, lifecycleId, t]);

  useEffect(() => {
    if (open) fetchHistory();
  }, [open, fetchHistory]);

  // Derived header info — pick first/latest by transaction_date (string-safe
  // sort because backend already returned ISO 8601 newest-first).
  const firstTxn = txns.length ? txns[txns.length - 1] : null;
  const latestTxn = txns.length ? txns[0] : null;
  const firstDate = _formatDate(firstTxn?.transaction_date, t).display;
  const latestDate = _formatDate(latestTxn?.transaction_date, t).display;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="transaction-history-modal"
        className="max-w-[820px] max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="w-5 h-5" />
            <span data-testid="txn-history-title">
              {t("portfolio.history.title").replace("{ticker}", ticker || "")}
            </span>
          </DialogTitle>
          <div className="flex items-center gap-3 text-xs text-muted-foreground flex-wrap">
            <Badge
              variant="outline"
              className={lifecycleStatus === "closed"
                ? "border-[#FF3DA6]/40 text-[#FF3DA6] bg-[#FF3DA6]/10 text-[10px]"
                : "border-[#2BFF8A]/40 text-[#2BFF8A] bg-[#2BFF8A]/10 text-[10px]"}
              data-testid="txn-history-lifecycle-badge"
            >
              {lifecycleStatus === "closed"
                ? t("portfolio.history.lifecycle_status_closed")
                : t("portfolio.history.lifecycle_status_open")}
            </Badge>
            {firstTxn ? (
              <span data-testid="txn-history-first">
                {t("portfolio.history.first_txn")}: <span className="tabular">{firstDate}</span>
              </span>
            ) : null}
            {latestTxn ? (
              <span data-testid="txn-history-latest">
                {t("portfolio.history.latest_txn")}: <span className="tabular">{latestDate}</span>
              </span>
            ) : null}
          </div>
        </DialogHeader>

        {loading ? (
          <div className="py-10 flex items-center justify-center text-sm text-muted-foreground" data-testid="txn-history-loading">
            <Loader2 className="w-4 h-4 mr-2 animate-spin" /> …
          </div>
        ) : error ? (
          <div
            data-testid="txn-history-error"
            className="rounded-lg border border-[#FF3DA6]/40 bg-[#FF3DA6]/10 px-3 py-3 text-xs text-[#FF7AC3] flex items-start gap-2"
          >
            <AlertCircle className="w-4 h-4 shrink-0 mt-[1px]" />
            <div className="flex-1 flex items-center justify-between gap-2">
              <span>{error}</span>
              <Button variant="outline" size="sm" onClick={fetchHistory} data-testid="txn-history-retry">
                {t("portfolio.history.retry")}
              </Button>
            </div>
          </div>
        ) : txns.length === 0 ? (
          <div className="py-10 text-center text-sm text-muted-foreground" data-testid="txn-history-empty">
            {t("portfolio.history.empty")}
          </div>
        ) : (
          <div className="overflow-x-auto" data-testid="txn-history-table-wrap">
            <TooltipProvider delayDuration={150}>
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent border-white/10">
                    <TableHead>{t("portfolio.history.col_date")}</TableHead>
                    <TableHead>{t("portfolio.history.col_type")}</TableHead>
                    <TableHead className="text-right">{t("portfolio.history.col_shares")}</TableHead>
                    <TableHead className="text-right">{t("portfolio.history.col_price")}</TableHead>
                    <TableHead className="text-right">{t("portfolio.history.col_fees")}</TableHead>
                    <TableHead className="text-right">{t("portfolio.history.col_gross")}</TableHead>
                    <TableHead className="text-right">{t("portfolio.history.col_net")}</TableHead>
                    <TableHead className="text-right">{t("portfolio.history.col_realized_pl")}</TableHead>
                    <TableHead>{t("portfolio.history.col_notes")}</TableHead>
                    {/* Source column hidden in the public view (Phase 8.C-2
                        polish). Admin viewers get the friendly + raw
                        codes inline via the Type badge tooltip. */}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {txns.map((tx) => (
                    <TxRow key={tx.transaction_id} tx={tx} currency={currency} t={t} isAdmin={isAdmin}
                      onDateSaved={async () => { await fetchHistory(); await onDateSaved?.(); }} />
                  ))}
                </TableBody>
              </Table>
            </TooltipProvider>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange?.(false)} data-testid="txn-history-close">
            {t("portfolio.history.close")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TxRow({ tx, currency, t, isAdmin, onDateSaved }) {
  const [editingDate, setEditingDate] = useState(false);
  const [date, setDate] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const isSell = (tx.transaction_type || "").toUpperCase() === "SELL";
  const cur = tx.currency || currency || "USD";
  const realized = tx.realized_pl;
  const gross = isSell ? tx.gross_amount : (Number(tx.shares || 0) * Number(tx.price_per_share || 0));
  const net = isSell ? tx.net_amount : (gross - Number(tx.fees || 0));
  const friendlySource = _friendlySource(tx.source, t);
  const dateInfo = _formatDate(tx.transaction_date, t);
  const canConfirmDate = !tx.transaction_date && tx.transaction_type === "BUY"
    && ["user_additional_purchase", "user_opening_lot"].includes(tx.source);
  const today = new Date().toISOString().slice(0, 10);
  const saveDate = async () => {
    if (!date || date > today || saving) return;
    setSaving(true);
    setSaveError(null);
    try {
      await api.patch(`/portfolio/transactions/${encodeURIComponent(tx.transaction_id)}/purchase-date`, {
        transaction_date: date,
      });
      setEditingDate(false);
      await onDateSaved?.();
    } catch (e) {
      const code = e?.response?.data?.detail?.error_code;
      setSaveError(t(code === "invalid_buy_date" ? "portfolio.dialog.err_buy_date"
        : code === "transaction_date_conflict" ? "portfolio.history.date_conflict"
        : "portfolio.history.date_save_error"));
    } finally {
      setSaving(false);
    }
  };
  return (
    <TableRow data-testid={`txn-history-row-${tx.transaction_id?.slice(0, 8)}`} className="border-white/10">
      <TableCell
        className={`tabular text-xs ${dateInfo.missing ? "text-muted-foreground italic" : ""}`}
        data-testid={`txn-history-date-${tx.transaction_id?.slice(0, 8)}`}
      >
        {dateInfo.display}
        {canConfirmDate && !editingDate ? (
          <Button variant="outline" size="sm" className="mt-1 block not-italic"
            data-testid="txn-add-purchase-date" onClick={() => setEditingDate(true)}>
            {t("portfolio.history.add_purchase_date")}
          </Button>
        ) : null}
        {canConfirmDate && editingDate ? (
          <div className="mt-2 min-w-[220px] space-y-2 not-italic" data-testid="txn-date-editor">
            <p>{t("portfolio.history.date_confirmation_hint")}</p>
            <Input type="date" value={date} max={today} required disabled={saving}
              aria-label={t("portfolio.dialog.buy_date")}
              onChange={(e) => setDate(e.target.value)} data-testid="txn-purchase-date-input" />
            {saveError ? <p role="alert" className="text-[#FF3DA6]">{saveError}</p> : null}
            <div className="flex gap-2">
              <Button size="sm" disabled={!date || date > today || saving} onClick={saveDate}
                data-testid="txn-purchase-date-save">
                {t(saving ? "portfolio.dialog.saving" : "portfolio.history.confirm_purchase_date")}
              </Button>
              <Button variant="outline" size="sm" disabled={saving} onClick={() => setEditingDate(false)}>
                {t("portfolio.dialog.cancel")}
              </Button>
            </div>
          </div>
        ) : null}
      </TableCell>
      <TableCell>
        <Tooltip>
          <TooltipTrigger asChild>
            <span
              className="inline-flex items-center gap-1 cursor-help"
              data-testid={`txn-history-type-tooltip-trigger-${tx.transaction_id?.slice(0, 8)}`}
            >
              <Badge
                variant="outline"
                className="border-white/20 text-white/90 bg-white/[0.04] text-[10px]"
                data-testid={`txn-history-type-${tx.transaction_id?.slice(0, 8)}`}
              >
                {isSell ? t("portfolio.history.type_sell") : t("portfolio.history.type_buy")}
              </Badge>
              <Info className="w-3 h-3 text-muted-foreground/70" />
            </span>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-[260px] text-xs">
            <div className="font-medium" data-testid={`txn-history-source-friendly-${tx.transaction_id?.slice(0, 8)}`}>
              {friendlySource}
            </div>
            {isAdmin && tx.source ? (
              <div
                className="mt-1 font-mono text-[10px] text-muted-foreground italic"
                data-testid={`txn-history-source-raw-${tx.transaction_id?.slice(0, 8)}`}
              >
                {t("portfolio.history.source_label")}: {tx.source}
              </div>
            ) : null}
          </TooltipContent>
        </Tooltip>
      </TableCell>
      <TableCell className="text-right tabular">{fmtNumber(tx.shares, { digits: 6 })}</TableCell>
      <TableCell className="text-right tabular">{fmtCurrency(tx.price_per_share, { currency: cur })}</TableCell>
      <TableCell className="text-right tabular text-muted-foreground">{fmtCurrency(tx.fees, { currency: cur })}</TableCell>
      <TableCell className="text-right tabular">{fmtCurrency(gross, { currency: cur })}</TableCell>
      <TableCell className="text-right tabular">{fmtCurrency(net, { currency: cur })}</TableCell>
      <TableCell className="text-right">
        {isSell && realized !== null && realized !== undefined ? (
          <span className={`tabular ${realized >= 0 ? "text-[#2BFF8A]" : "text-[#FF3DA6]"}`}>
            {fmtCurrency(realized, { currency: cur, signed: true })}
          </span>
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </TableCell>
      <TableCell className="text-xs text-muted-foreground max-w-[160px] truncate" title={tx.notes || ""}>
        {tx.notes || "—"}
      </TableCell>
    </TableRow>
  );
}
